// sidebar/components/Links.tsx
/* eslint-disable */
import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
// Import AuthContext to access authentication state
import { AuthContext } from "context/AuthContext";
// Import the LogoutButton component
import LogoutButton from "../../LogoutButton";
import { MdLock } from "react-icons/md";

// Define RoutesType if not already defined
interface RoutesType {
  name: string;
  //layout: string;
  path: string;
  icon: React.ReactElement;
  component: React.ReactElement;
  secondary?: boolean;
}

/**
 * Clases compartidas por cada item de navegacion. El estado activo se resuelve
 * con fondo sutil + texto solido + una barra de acento a la izquierda, en vez
 * de cambiar el peso tipografico (que hacia saltar el ancho del texto).
 */
export const navItemClass = (isActive: boolean) =>
  [
    "group relative flex h-10 items-center gap-3 rounded-control px-3",
    "text-sm transition-colors duration-150",
    isActive
      ? "bg-gray-100 font-semibold text-gray-900 dark:bg-white/10 dark:text-white"
      : "font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white",
  ].join(" ");

export const navIconClass = (isActive: boolean) =>
  [
    "flex shrink-0 items-center text-[18px] transition-colors duration-150",
    isActive
      ? "text-brand-500 dark:text-brand-200"
      : "text-gray-600 group-hover:text-gray-700 dark:text-gray-500 dark:group-hover:text-gray-300",
  ].join(" ");

/** Barra de acento del item activo */
export const NavActiveBar = () => (
  <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-500 dark:bg-brand-200" />
);

export const SidebarLinks: React.FC<{ routes: RoutesType[] }> = ({ routes }) => {
  // Access the current location
  const location = useLocation();
  // Consume AuthContext to get authentication status
  const { isLoggedIn } = useContext(AuthContext);

  // Function to check if the route is active
  function isRouteActive(routePath: string): boolean {
    if (routePath === "") {
      // Means the "main page" -> highlight only if user is exactly at "/"
      return location.pathname === "/";
    } else {
      // If the user path starts with "/" + routePath, highlight it.
      return location.pathname.startsWith("/" + routePath);
    }
  }

  // Function to create navigation links
  const createLinks = (routes: RoutesType[]) => {
    return routes.map((route, index) => {
      // **Exclude "Iniciar Sesion" from the routes mapping to prevent duplication**
      if (route.name === "Iniciar Sesión") {
        return null;
      }
      const linkPath =
        route.path === ""
          ? "/" // main page
          : `/${route.path}`;

      const isActive = isRouteActive(route.path);

      return (
        <li key={index}>
          <Link
            to={linkPath}
            aria-current={isActive ? "page" : undefined}
            className={navItemClass(isActive)}
          >
            {isActive && <NavActiveBar />}
            <span className={navIconClass(isActive)}>
              {route.icon ? route.icon : <DashIcon />}
            </span>
            <span className="truncate">{route.name}</span>
          </Link>
        </li>
      );
    });
  };

  return (
    <>
      {/* Render the filtered links */}
      {createLinks(routes)}

      {/* **Conditionally Render "Iniciar Sesion" or "Cerrar Sesion"** */}
      {isLoggedIn ? (
        // Render "Cerrar Sesion" button when authenticated
        <LogoutButton variant="sidebar" />
      ) : (
        // Render "Iniciar Sesion" link when not authenticated
        <li className="login-sidebar">
          <Link to="/inicio-sesion" className={navItemClass(false)}>
            <span className={navIconClass(false)}>
              <MdLock className="h-[18px] w-[18px]" />
            </span>
            <span className="truncate">Iniciar Sesión</span>
          </Link>
        </li>
      )}
    </>
  );
};

export default SidebarLinks;
